import { useState, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { Tab } from '@constants/surveys';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { useTranslation } from '@hooks';
import { InfoOutlinedIcon } from 'assets/images/icons';
import Rate from './Rate';
import Positive from './Positive';
import Negative from './Negative';
import Questions from './Questions';

const IFrameWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'height',
})(({ height }) => ({
    height: height ? height : 'calc(100% - 48px)',
    width: 320,
    margin: '0 auto',
    '@media (max-width: 768px)': {
        width: 320,
        minHeight: 520,
    },
    '@media (max-width: 420px)': {
        width: '100%',
        paddingBottom: 0,
    },
}));

export const PreviewTabs = styled(RadioButtonsGroup)({
    width: 252,
    display: 'block',
    margin: '0 auto 16px',
    '& .MuiFormControlLabel-root': {
        height: 32,
        padding: '0 4px',
    },
});

const ViewMap = {
    Rate: 0,
    Positive: 1,
    Negative: 2,
    SurveyDisabled: 3,
    Questions: 4,
};

const Preview = ({ account, externalId, tab }) => {
    const { t } = useTranslation();
    const [view, setView] = useState(ViewMap.Rate);
    const [height, setHeight] = useState('100%');
    const { watch } = useFormContext();

    const mainLocationId = watch('mainLocationId');
    const locationType = watch('locationType');
    const satisfactionSurveyEnabled = watch('satisfactionSurveyEnabled');
    const questions = watch('questions');

    useEffect(() => {
        const controller = new AbortController();
        const setPreviewData = event => {
            if (event.origin === process.env.REACT_APP_SURVEY_DOMAIN) {
                const message = event.data;
                if (message.type === 'pl-preview-iframe-loaded') {
                    setHeight(message.data.iframeHeight);
                }
            }
        };
        window.addEventListener('message', setPreviewData, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const currentView = tab === Tab.Questions ? ViewMap.Questions : ViewMap.Rate;
        setHeight(520);
        if (!satisfactionSurveyEnabled && tab !== Tab.Questions) {
            setView(ViewMap.SurveyDisabled);
        } else {
            setView(currentView);
        }
    }, [satisfactionSurveyEnabled, tab]);

    const previewLocation = useMemo(
        () => account?.companies?.find(c => c.companyId === mainLocationId) || {},
        [mainLocationId, account],
    );

    const locationSurveyId = `${externalId}${locationType === LOCATION_TYPE.BRAND ? `:${mainLocationId}` : ''}`;
    const hasQuestions = Object.keys(questions).length > 0;

    return (
        <>
            {view !== ViewMap.SurveyDisabled && view !== ViewMap.Questions && (
                <PreviewTabs
                    value={view}
                    onChange={value => {
                        setHeight(520);
                        setView(value);
                    }}
                    options={[
                        { value: ViewMap.Rate, label: t('FeedbackSurveys.main') },
                        { value: ViewMap.Positive, label: t('FeedbackSurveys.positive') },
                        { value: ViewMap.Negative, label: t('FeedbackSurveys.negative') },
                    ]}
                />
            )}
            {view === ViewMap.Questions && !hasQuestions ? (
                <Typography
                    variant="caption"
                    sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', marginTop: '16px', textAlign: 'center' }}
                >
                    {t('FeedbackSurveys.addFirstQuestion')}
                </Typography>
            ) : (
                <>
                    <div>
                        <IFrameWrapper height={height}>
                            {view === ViewMap.Rate && (
                                <Rate locationSurveyId={locationSurveyId} location={previewLocation} />
                            )}
                            {view === ViewMap.Questions && hasQuestions && (
                                <Questions locationSurveyId={locationSurveyId} location={previewLocation} />
                            )}
                            {(view === ViewMap.Positive || view === ViewMap.SurveyDisabled) && (
                                <Positive locationSurveyId={locationSurveyId} location={previewLocation} />
                            )}
                            {view === ViewMap.Negative && (
                                <Negative locationSurveyId={locationSurveyId} view={view} location={previewLocation} />
                            )}
                        </IFrameWrapper>
                    </div>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', marginTop: '16px', textAlign: 'center' }}
                    >
                        <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                        <Trans t={t} i18nKey={'FeedbackSurveys.changeAppearance'}>
                            <InternalLink
                                to="/account-settings/brands"
                                sx={{ color: '#1F4C5C', cursor: 'pointer', textDecoration: 'none' }}
                            >
                                {{ brandSettings: t('FeedbackSurveys.brandSettings') }}
                            </InternalLink>
                        </Trans>
                    </Typography>
                </>
            )}
        </>
    );
};

export default Preview;
